"use client";

import type { scaleBand } from "@visx/scale";
import type { MotionValue, Transition } from "motion/react";
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
} from "motion/react";
import { memo, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { barSlotKey, useBarProjection } from "./bar-projection-context";
import {
  chartCssVars,
  type TooltipData,
  useChart,
  useChartStable,
  useYScale,
} from "./chart-context";
import { useChartLegendHover } from "./chart-legend-hover";
import { transitionWithDelay } from "./motion-utils";
import {
  type Box3,
  barBox,
  barRiseAt,
  barRow,
  boxPaths,
  boxTopCenter,
  type ChartProjection,
  FACE_FRONT,
  FACE_SIDE,
  FRONT_SHADE,
  lerp,
  ProjectedAxisLabels,
  SIDE_SHADE,
  shadeFill,
  useChartProjection,
  useProjectionProgress,
} from "./projection";

type ScaleBand<Domain extends { toString(): string }> = ReturnType<
  typeof scaleBand<Domain>
>;

export type BarLineCap = "round" | "butt" | number;
export type BarAnimationType = "grow" | "fade";

export interface BarProps {
  /** Key in data to use for y values */
  dataKey: string;
  /** Y-scale group id for vertical bars (Recharts `yAxisId`). Default: `"left"`. */
  yAxisId?: string | number;
  /** Fill color for the bar. Can be a color, gradient url, or pattern url. Default: var(--chart-line-primary) */
  fill?: string;
  /** Color for tooltip dot. Use when fill is a gradient/pattern. Default: uses fill value */
  stroke?: string;
  /** Line cap style for bar ends: "round", "butt", or a number for custom radius. Default: "round" */
  lineCap?: BarLineCap;
  /** Whether to animate the bars. Default: true */
  animate?: boolean;
  /** Animation type: "grow" (height) or "fade" (opacity + blur). Default: "grow" */
  animationType?: BarAnimationType;
  /** Opacity when not hovered (when another bar is hovered). Default: 0.3 */
  fadedOpacity?: number;
  /** Stagger delay between bars in seconds. Auto-calculated if not provided. */
  staggerDelay?: number;
  /** Gap between stacked bars in pixels. Default: 0 */
  stackGap?: number;
  /** Gap between grouped bars in pixels. Default: 4 */
  groupGap?: number;
  label?: string;
}

interface BarInnerProps extends BarProps {
  barScale: ScaleBand<string>;
  bandWidth: number;
  barXAccessor: (d: Record<string, unknown>) => string;
}

interface AnimatedBarProps {
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  rx: number;
  ry: number;
  index: number;
  isFaded: boolean;
  animationType: BarAnimationType;
  innerHeight: number;
  fadedOpacity: number;
  staggerDelay: number;
  enterTransition?: Transition;
  revealEpoch: number;
  isHorizontal: boolean;
}

function AnimatedBar({
  x,
  y,
  width,
  height,
  fill,
  rx,
  ry,
  index,
  isFaded,
  animationType,
  innerHeight,
  fadedOpacity,
  staggerDelay,
  enterTransition,
  revealEpoch,
  isHorizontal,
}: AnimatedBarProps) {
  const enterAnim = transitionWithDelay(enterTransition, index * staggerDelay);

  if (animationType === "fade") {
    return (
      <motion.rect
        animate={{
          opacity: isFaded ? fadedOpacity : 1,
          filter: "blur(0px)",
        }}
        fill={fill}
        height={height}
        initial={{ opacity: 0, filter: "blur(2px)" }}
        key={`fade-${index}-${revealEpoch}`}
        rx={rx}
        ry={ry}
        transition={enterAnim}
        width={width}
        x={x}
        y={y}
      />
    );
  }

  const initial = isHorizontal
    ? { width: 0, height, x: 0, y }
    : { width, height: 0, x, y: innerHeight };
  const target = isHorizontal
    ? { width, height, x: 0, y }
    : { width, height, x, y };

  return (
    <g
      opacity={isFaded ? fadedOpacity : 1}
      style={{ transition: "opacity 0.15s ease-in-out" }}
    >
      <motion.rect
        animate={target}
        fill={fill}
        initial={initial}
        key={`grow-${index}-${revealEpoch}`}
        rx={rx}
        ry={ry}
        transition={enterAnim}
      />
    </g>
  );
}

interface ProjectedBarProps {
  projection: ChartProjection;
  reveal: MotionValue<number>;
  flat: Box3;
  iso: Box3;
  category: number;
  categories: number;
  laneSeries: number;
  laneCount: number;
  fill: string;
  isFaded: boolean;
  fadedOpacity: number;
  hovered: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onTouchStart?: () => void;
}

const morphBarBox = (flat: Box3, iso: Box3, e: number, rise: number): Box3 => ({
  x0: lerp(flat.x0, iso.x0, e),
  x1: lerp(flat.x1, iso.x1, e),
  y0: iso.y0,
  y1: iso.y1,
  z0: flat.z0 * rise,
  z1: flat.z1 * rise,
});

function ProjectedBar({
  projection,
  reveal,
  flat,
  iso,
  category,
  categories,
  laneSeries,
  laneCount,
  fill,
  isFaded,
  fadedOpacity,
  hovered,
  onMouseEnter,
  onMouseLeave,
  onTouchStart,
}: ProjectedBarProps) {
  const { e } = projection;
  const paths = useTransform([e, reveal], ([progress = 0, t = 1]: number[]) =>
    boxPaths(
      projection.at(progress),
      morphBarBox(
        flat,
        iso,
        progress,
        barRiseAt(t, category, categories, laneSeries, laneCount)
      )
    )
  );
  const top = useTransform(paths, (p) => p.top);
  const front = useTransform(paths, (p) => (p.faces & FACE_FRONT ? p.front : ""));
  const side = useTransform(paths, (p) => (p.faces & FACE_SIDE ? p.side : ""));
  const frontFill = useTransform(e, (v) =>
    shadeFill(fill, lerp(100, FRONT_SHADE, v))
  );
  const sideFill = useTransform(e, (v) =>
    shadeFill(fill, lerp(100, SIDE_SHADE, v))
  );

  return (
    <g
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      opacity={isFaded ? fadedOpacity : 1}
      style={{ transition: "opacity 0.15s ease-in-out" }}
    >
      <motion.path d={side} style={{ fill: sideFill }} />
      <motion.path d={front} style={{ fill: frontFill }} />
      <motion.path
        d={top}
        fill={fill}
        stroke={hovered ? chartCssVars.foreground : "none"}
        strokeLinejoin="round"
        strokeWidth={hovered ? 1 : 0}
        vectorEffect="non-scaling-stroke"
      />
    </g>
  );
}

const BarInner = memo(function BarInner({
  dataKey,
  yAxisId,
  fill = chartCssVars.linePrimary,
  lineCap = "round",
  animate = true,
  animationType = "grow",
  fadedOpacity = 0.3,
  staggerDelay,
  stackGap = 0,
  groupGap = 4,
  label,
  barScale,
  bandWidth,
  barXAccessor,
}: BarInnerProps) {
  const {
    data,
    yScale: chartYScale,
    innerHeight,
    innerWidth,
    isLoaded,
    hoveredBarIndex,
    tooltipData,
    lines,
    orientation,
    stacked,
    stackOffsets,
    animationDuration,
    enterTransition,
    revealEpoch = 0,
  } = useChart();
  const projection = useChartProjection();
  const barProjection = useBarProjection();
  const settledReveal = useMotionValue(1);
  const projectionProgress = useProjectionProgress(projection);
  const [progressActive, setProgressActive] = useState(
    () => projectionProgress.get() > 0
  );
  useMotionValueEvent(projectionProgress, "change", (value) =>
    setProgressActive(value > 0)
  );
  const hoverCapable = useHoverCapable();

  // Calculate stagger delay automatically if not provided
  // Total animation duration is ~1200ms, with 40% for stagger spread and 60% for bar animation
  const totalAnimDuration = animationDuration || 1100;
  const staggerSpread = totalAnimDuration * 0.4; // 40% of time for stagger spread
  const calculatedStaggerDelay =
    staggerDelay ?? (data.length > 1 ? staggerSpread / 1000 / data.length : 0);
  const uniqueId = useId();

  const isHorizontal = orientation === "horizontal";

  // Find the index of this bar series among all bar series
  // filterIndex, not selectedIndex: only a legend selection may single out a
  // series. An element tap neither hides the others nor marks the legend row.
  const { filterIndex: legendActiveIndex, toggleSelectedIndex } =
    useChartLegendHover();

  const seriesIndex = useMemo(() => {
    const idx = lines.findIndex((l) => l.dataKey === dataKey);
    return idx >= 0 ? idx : 0;
  }, [lines, dataKey]);

  const seriesConfig = lines[seriesIndex];
  const valueScale = useYScale(yAxisId ?? seriesConfig?.yAxisId);

  const isLegendDimmed =
    legendActiveIndex !== null && legendActiveIndex !== seriesIndex;

  const seriesCount = lines.length;
  const isLastSeries = seriesIndex === seriesCount - 1;

  // Calculate the width for each bar within a group (for non-stacked)
  const barWidth = useMemo(() => {
    if (!bandWidth || seriesCount === 0) {
      return 0;
    }
    if (stacked) {
      // Stacked bars use full band width
      return bandWidth;
    }
    // Leave a gap between grouped bars (controlled by groupGap prop)
    const effectiveGroupGap = seriesCount > 1 ? groupGap : 0;
    return (bandWidth - effectiveGroupGap * (seriesCount - 1)) / seriesCount;
  }, [bandWidth, seriesCount, stacked, groupGap]);

  const cornerRadius = useMemo(() => {
    if (typeof lineCap === "number") {
      return lineCap;
    }
    if (lineCap === "round" && barWidth) {
      return Math.min(barWidth / 2, 8);
    }
    return 0;
  }, [lineCap, barWidth]);

  const projected =
    projection !== null &&
    barProjection !== null &&
    !isHorizontal &&
    (projection.isometric ||
      !projection.settled ||
      progressActive ||
      projectionProgress.get() > 0);
  const laneSeries = stacked ? 0 : seriesIndex;
  const laneCount = stacked ? 1 : Math.max(1, seriesCount);
  const lane = barRow(laneSeries, laneCount);
  const categoryStep =
    (barScale as unknown as { step?: () => number }).step?.() ?? bandWidth;
  const laneDepth = barProjection?.laneDepth ?? 0;
  const reveal = animate && barProjection ? barProjection.reveal : settledReveal;
  const showLaneLabel =
    projection !== null &&
    barProjection !== null &&
    !isHorizontal &&
    !stacked &&
    seriesCount > 1 &&
    Boolean(label);

  return (
    <g
      className={`bar-series-${uniqueId}`}
      onClick={(e) =>
        toggleSelectedIndex(seriesIndex, {
          element: e.currentTarget,
          kind: "series",
        })
      }
      style={{ cursor: "pointer" }}
    >
      {showLaneLabel && (
        <ProjectedAxisLabels
          axis="y"
          dx={6}
          dy={4}
          labels={[
            {
              key: dataKey,
              text: label ?? "",
              x: innerWidth,
              y: (lane + 0.5) * laneDepth,
              z: 0,
            },
          ]}
          textAnchor="start"
        />
      )}
      {data.map((d, i) => {
        const value = d[dataKey];
        if (typeof value !== "number") {
          return null;
        }

        const categoryValue = barXAccessor(d);
        const bandPos = barScale(categoryValue) ?? 0;

        let x: number;
        let y: number;
        let barHeight: number;
        let barW: number;

        const scale = isHorizontal ? chartYScale : valueScale;

        if (isHorizontal) {
          // Horizontal bars: category on y-axis, value on x-axis
          const valuePos = scale(value) ?? 0;
          barW = valuePos; // Width is the value position (grows from left)
          barHeight = barWidth;

          if (stacked && stackOffsets) {
            const offset = stackOffsets.get(i)?.get(dataKey) ?? 0;
            x = scale(offset) ?? 0;
            barW = valuePos - x;
            // Apply stack gap for horizontal: shift right and reduce width
            const gapOffset = seriesIndex * stackGap;
            x += gapOffset;
            if (!isLastSeries && stackGap > 0) {
              barW = Math.max(0, barW - stackGap);
            }
          } else {
            x = 0;
            // For grouped bars, offset y position
            const effectiveGroupGap = seriesCount > 1 ? groupGap : 0;
            y = bandPos + seriesIndex * (barWidth + effectiveGroupGap);
          }
          y = stacked
            ? bandPos
            : bandPos +
              seriesIndex * (barWidth + (seriesCount > 1 ? groupGap : 0));
        } else {
          // Vertical bars: category on x-axis, value on y-axis
          const valuePos = scale(value) ?? 0;
          barHeight = innerHeight - valuePos;
          barW = barWidth;

          if (stacked && stackOffsets) {
            const offset = stackOffsets.get(i)?.get(dataKey) ?? 0;
            const offsetY = scale(offset) ?? innerHeight;
            // Apply stack gap: shift up and reduce height
            const gapOffset = seriesIndex * stackGap;
            y = offsetY - barHeight - gapOffset;
            // Reduce height slightly for non-last bars to create visual gap
            if (!isLastSeries && stackGap > 0) {
              barHeight = Math.max(0, barHeight - stackGap);
            }
          } else {
            y = valuePos;
            // For grouped bars, offset x position
            const effectiveGroupGap = seriesCount > 1 ? groupGap : 0;
            x = bandPos + seriesIndex * (barWidth + effectiveGroupGap);
          }
          x = stacked
            ? bandPos
            : bandPos +
              seriesIndex * (barWidth + (seriesCount > 1 ? groupGap : 0));
        }

        const isFaded =
          (hoveredBarIndex !== null && hoveredBarIndex !== i) || isLegendDimmed;

        // Use categoryValue as key since it's the unique identifier from data
        const barKey = `bar-${dataKey}-${categoryValue}`;

        if (projected && projection && barProjection) {
          const slot = barProjection.slots.get(barSlotKey(i, lane));
          if (!slot) {
            return null;
          }
          const zBottom = innerHeight - (y + barHeight);
          const zTop = innerHeight - y;
          const z0 = Math.max(0, Math.min(zBottom, zTop));
          const z1 = Math.max(0, zBottom, zTop);
          const footprint = barBox(i, laneSeries, laneCount, 1);
          const center = bandPos + bandWidth / 2;
          const flat: Box3 = {
            x0: x,
            x1: x + barW,
            y0: footprint.y0 * laneDepth,
            y1: footprint.y1 * laneDepth,
            z0,
            z1,
          };
          const iso: Box3 = {
            x0: center + (footprint.x0 - i - 0.5) * categoryStep,
            x1: center + (footprint.x1 - i - 0.5) * categoryStep,
            y0: flat.y0,
            y1: flat.y1,
            z0,
            z1,
          };
          const anchor = boxTopCenter(projection.at(1), iso);
          const tooltip = (): TooltipData => ({
            point: d,
            index: i,
            x: anchor[0],
            yPositions: { [dataKey]: anchor[1] },
            xPositions: { [dataKey]: anchor[0] },
          });
          const interactive = barProjection.interactive;
          const hovered =
            interactive &&
            tooltipData?.index === i &&
            tooltipData.xPositions?.[dataKey] === anchor[0];
          return createPortal(
            <ProjectedBar
              categories={data.length}
              category={i}
              fadedOpacity={fadedOpacity}
              fill={fill}
              flat={flat}
              hovered={hovered}
              isFaded={isFaded}
              iso={iso}
              laneCount={laneCount}
              laneSeries={laneSeries}
              onMouseEnter={
                interactive && hoverCapable
                  ? () => barProjection.enter(tooltip())
                  : undefined
              }
              onMouseLeave={
                interactive && hoverCapable ? barProjection.leave : undefined
              }
              onTouchStart={
                interactive ? () => barProjection.tap(tooltip()) : undefined
              }
              projection={projection}
              reveal={reveal}
            />,
            slot,
            barKey
          );
        }

        // Apply rounded corners:
        // - For non-stacked: always apply
        // - For stacked with gap: apply to all bars
        // - For stacked without gap: only apply to the last series
        const applyRounding = !stacked || stackGap > 0 || isLastSeries;
        const effectiveRx = applyRounding ? cornerRadius : 0;
        const effectiveRy = applyRounding ? cornerRadius : 0;

        if (animate && !isLoaded) {
          return (
            <AnimatedBar
              animationType={animationType}
              enterTransition={enterTransition}
              fadedOpacity={fadedOpacity}
              fill={fill}
              height={barHeight}
              index={i}
              innerHeight={innerHeight}
              isFaded={isFaded}
              isHorizontal={isHorizontal}
              key={barKey}
              revealEpoch={revealEpoch}
              rx={effectiveRx}
              ry={effectiveRy}
              staggerDelay={calculatedStaggerDelay}
              width={barW}
              x={x}
              y={y}
            />
          );
        }

        // Static bar after animation completes
        return (
          <rect
            fill={fill}
            height={barHeight}
            key={barKey}
            opacity={isFaded ? fadedOpacity : 1}
            rx={effectiveRx}
            ry={effectiveRy}
            style={{
              cursor: "default",
              transition: "opacity 0.15s ease-in-out",
            }}
            width={barW}
            x={x}
            y={y}
          />
        );
      })}
    </g>
  );
});

export function Bar(props: BarProps) {
  const { barScale, bandWidth, barXAccessor } = useChartStable();

  if (!(barScale && bandWidth && barXAccessor)) {
    console.warn("Bar component must be used within a BarChart");
    return null;
  }

  return (
    <BarInner
      {...props}
      bandWidth={bandWidth}
      barScale={barScale}
      barXAccessor={barXAccessor}
    />
  );
}

Bar.displayName = "Bar";

export default Bar;
